// Publishing an Audiotool project to Seedbed, from the browser.
//
// The work itself — capture, cut, hash, mix, encode, upload, link — is the
// extension's (@audiotool-core/project-upload), shared so a project lands the
// same way from Live and from a tab. What is browser-specific is how the tab
// reaches Seedbed, and there are two:
//
//   • the admin visualizer: the producer's own Seedbed session (seedbed-auth)
//     through viz/worker/audiotool.ts;
//   • the web app (seedbed-web): the account session (webApi) through the
//     core API forward and the account worker's object route.
//
// Either way the bytes go through this app's OWN worker, which forwards them
// to the presigned R2 URL — the bucket has no CORS policy, and this keeps the
// signed URL as the only authorisation involved.

import type { AuthenticatedClient } from "@audiotool/nexus";
import type { SeedbedTransport } from "@audiotool-core/project-upload.js";
import { renderApi, type RenderApi } from "@audiotool-core/render.js";
import { authedFetch } from "@web-shared/lib/seedbed-auth";
import { json, webApi } from "@web-shared/lib/web-api";

export {
  describeCapture,
  sha256Hex,
  uploadProjectToSeedbed,
  type ProjectUploadOutcome,
  type ProjectUploadRequest,
  type SeedbedTransport,
} from "@audiotool-core/project-upload.js";

const PUT_RETRIES = 3;

/**
 * Audiotool's renders, from this tab — the master and each MIDI track's sound
 * come from Audiotool itself (@audiotool-core/render). Its hosts are CORS-open.
 */
export function browserRenders(client: AuthenticatedClient): RenderApi {
  return renderApi({ projects: client.projects, getToken: async () => client.exportTokens().accessToken });
}

/** The admin visualizer: its own Seedbed session, its /api/audiotool forward. */
export const adminTransport: SeedbedTransport = {
  async postJson<T>(path: string, body: unknown): Promise<T> {
    const res = await authedFetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    const parsed: unknown = text ? JSON.parse(text) : {};
    if (!res.ok) {
      const message =
        typeof parsed === "object" && parsed !== null && "error" in parsed ? String((parsed as { error: unknown }).error) : `HTTP ${res.status}`;
      throw new Error(message);
    }
    return parsed as T;
  },
  put: (url, bytes, label, headers) => putVia("/api/audiotool/object", url, bytes, label, headers),
};

/** The web app: the account session, the core API forward, the account object route. */
export const webTransport: SeedbedTransport = {
  postJson: <T>(path: string, body: unknown): Promise<T> => webApi<T>(path, json("POST", body)),
  put: (url, bytes, label, headers) => putVia("/api/account/object", url, bytes, label, headers),
};

/**
 * PUT through this app's own worker, which forwards to the presigned URL along
 * with the headers signed into it (a pool object's checksum — R2 refuses any
 * other bytes). A failure re-sends the whole object.
 */
export async function putVia(route: string, url: string, bytes: Uint8Array, label: string, headers: Record<string, string> = {}): Promise<void> {
  let lastErr: unknown;
  for (let attempt = 1; attempt <= PUT_RETRIES; attempt++) {
    try {
      const res = await fetch(route, {
        method: "PUT",
        headers: { ...headers, "X-Upload-Url": url, "Content-Type": "application/octet-stream" },
        body: Uint8Array.from(bytes) as unknown as BodyInit,
      });
      if (!res.ok) {
        // The forwarding worker says what R2 answered; a bare status says nothing.
        const detail = await res
          .json()
          .then((j: unknown) => (j && typeof j === "object" && typeof (j as { error?: unknown }).error === "string" ? (j as { error: string }).error : null))
          .catch(() => null);
        throw new Error(`PUT ${label} failed (${res.status})${detail ? `: ${detail}` : ""}`);
      }
      return;
    } catch (err) {
      lastErr = err;
      if (attempt < PUT_RETRIES) await new Promise((r) => setTimeout(r, attempt * 1000));
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error(`PUT ${label} failed`);
}
